/**
 * ============================================================================
 * THE SHOWROOM v4.0 — DEPARTURE LOUNGE
 * Cross-Collection Experience Card System
 * ============================================================================
 *
 * FILE: showroom-departure-lounge.js (standalone)
 *
 * INSTALLATION:
 * 1. Upload to Shopify Assets
 * 2. Add to the-showroom.liquid AFTER the main showroom JS:
 *    <script src="{{ 'showroom-departure-lounge.js' | asset_url }}" defer></script>
 *
 * 3. Add these 2 edits to showroom-v3.js:
 *
 *    EDIT A — Top of showCollection(), add early return:
 *
 *      function showCollection() {
 *        if (activeCollection === 'departure-lounge') {
 *          showDepartureLounge();
 *          return;
 *        }
 *        // ... existing code ...
 *      }
 *
 *    EDIT B — In category button click handler, before activeCollection = collection:
 *
 *      resetDepartureState();
 *
 * ============================================================================
 */

(function() {
  'use strict';

  /* ========================================================================
     STATE
     ======================================================================== */

  window.departureState = {
    selectedCard: null,
    selectedHook: null,
    linkedHandles: [],
    mode: 'cards',  // 'cards' or 'products'
    originalOrder: null,  // snapshot of grid child order before rearranging
    originalBtnHTML: {}   // stores original button innerHTML per card name
  };


  /* ========================================================================
     MAIN RENDER — called from showCollection() via early return
     ======================================================================== */

  window.showDepartureLounge = function() {
    const productGrid = document.getElementById('productGrid');
    const noResultsEl = document.getElementById('noResults');

    // Snapshot original DOM order before first rearrange
    if (!departureState.originalOrder) {
      departureState.originalOrder = Array.from(productGrid.children);
    }

    productGrid.classList.add('fading');

    setTimeout(function() {
      const allCards = document.querySelectorAll('.product-card');

      if (departureState.mode === 'cards') {
        renderCardsView(allCards, productGrid);
      } else {
        renderProductsView(allCards, productGrid);
      }

      if (noResultsEl) noResultsEl.classList.remove('active');
      productGrid.classList.remove('fading');
      productGrid.classList.remove('showroom-loading');

      // Re-init carousels if the function exists
      if (typeof initializeCarousels === 'function') initializeCarousels();
    }, 150);
  };


  /* ========================================================================
     CARDS VIEW — show experience cards, hide everything else
     ======================================================================== */

  function renderCardsView(allCards, productGrid) {
    var sceneBar = document.getElementById('departureSceneBar');
    var experienceBar = document.getElementById('departureExperienceBar');

    if (sceneBar) sceneBar.style.display = 'none';
    if (experienceBar) experienceBar.style.display = 'none';

    // Restore original DOM order
    if (departureState.originalOrder) {
      departureState.originalOrder.forEach(function(child) {
        productGrid.appendChild(child);
      });
    }

    // Restore departure card buttons to original state
    restoreCardButtons();

    // Restore price badges to original static totals
    restorePriceBadges();

    departureState.selectedCard = null;
    departureState.selectedHook = null;
    departureState.linkedHandles = [];
    departureState.mode = 'cards';

    allCards.forEach(function(card) {
      if (card.classList.contains('cart-view-card')) return;
      if (card.classList.contains('departure-card')) {
        card.classList.remove('hidden');
      } else {
        card.classList.add('hidden');
      }
    });
  }


  /* ========================================================================
     PRODUCTS VIEW — one card selected, linked products from ALL collections
     ======================================================================== */

  function renderProductsView(allCards, productGrid) {
    var sceneBar = document.getElementById('departureSceneBar');
    var sceneTitle = document.getElementById('departureSceneTitle');

    // Scene bar
    if (sceneBar) sceneBar.style.display = '';
    if (sceneTitle) {
      sceneTitle.innerHTML =
        '<span class="scene-card-name">' + escapeHtml(departureState.selectedCard) + '</span>' +
        ' <span class="scene-hook-separator">—</span> ' +
        '<span class="scene-hook-text">' + escapeHtml(departureState.selectedHook) + '</span>';
    }

    // Build handle lookup
    var handleSet = {};
    departureState.linkedHandles.forEach(function(h) {
      handleSet[h.trim().toLowerCase()] = true;
    });

    var selectedCardEl = null;
    var visibleProducts = [];

    allCards.forEach(function(card) {
      if (card.classList.contains('cart-view-card')) return;

      // Experience cards: keep selected one, hide the rest
      if (card.classList.contains('departure-card')) {
        var cardName = card.dataset.cardName || '';
        if (cardName === departureState.selectedCard) {
          card.classList.remove('hidden');
          selectedCardEl = card;
        } else {
          card.classList.add('hidden');
        }
        return;
      }

      // Cross-collection handle match
      var cardHandle = (card.dataset.productHandle || '').toLowerCase();
      if (cardHandle && handleSet[cardHandle]) {
        card.classList.remove('hidden');
        visibleProducts.push(card);
      } else {
        card.classList.add('hidden');
      }
    });

    // Sort products alphabetically
    visibleProducts.sort(function(a, b) {
      var tA = (a.querySelector('.product-card-header-title') || {}).textContent || '';
      var tB = (b.querySelector('.product-card-header-title') || {}).textContent || '';
      return tA.toLowerCase().localeCompare(tB.toLowerCase());
    });

    // Pin the selected experience card first, then products
    if (selectedCardEl) productGrid.appendChild(selectedCardEl);
    visibleProducts.forEach(function(card) { productGrid.appendChild(card); });

    // Transform departure card buttons into Add action
    transformCardButtons(selectedCardEl);

    // Update both the experience bar (desktop) and card buttons (mobile)
    updateExperienceBar();

    // Screen reader announcement
    if (typeof announceFilterResults === 'function') announceFilterResults();
  }


  /* ========================================================================
     DYNAMIC EXPERIENCE BAR — recalculates from active toggles & stock
     ======================================================================== */

  function getExperiencePricing() {
    var handleSet = {};
    departureState.linkedHandles.forEach(function(h) {
      handleSet[h.trim().toLowerCase()] = true;
    });

    var totalProducts = 0;
    var availableCount = 0;
    var totalPrice = 0;

    document.querySelectorAll('.product-card:not(.hidden):not(.departure-card):not(.cart-view-card)').forEach(function(card) {
      var handle = (card.dataset.productHandle || '').toLowerCase();
      if (!handleSet[handle]) return;

      totalProducts++;

      var activeToggle = card.querySelector('.size-toggle-btn.active');
      if (!activeToggle) return;

      var isAvailable = activeToggle.dataset.variantAvailable === 'true';
      if (isAvailable) {
        availableCount++;
        var price = parseInt(activeToggle.dataset.variantPrice) || 0;
        totalPrice += price;
      }
    });

    return {
      totalProducts: totalProducts,
      availableCount: availableCount,
      totalPrice: totalPrice
    };
  }

  function updateExperienceBar() {
    var experienceBar = document.getElementById('departureExperienceBar');
    var productCountEl = document.getElementById('departureProductCount');
    var addBtn = document.getElementById('departureAddExperienceBtn');

    // Only run in products mode
    if (departureState.mode !== 'products') {
      if (experienceBar) experienceBar.style.display = 'none';
      return;
    }

    var pricing = getExperiencePricing();

    // Update desktop experience bar
    if (experienceBar) {
      if (pricing.totalProducts === 0) {
        experienceBar.style.display = 'none';
      } else {
        experienceBar.style.display = '';

        if (productCountEl) {
          if (pricing.availableCount === pricing.totalProducts) {
            productCountEl.textContent = pricing.totalProducts + ' product' + (pricing.totalProducts !== 1 ? 's' : '');
          } else {
            productCountEl.textContent = pricing.availableCount + ' of ' + pricing.totalProducts + ' available';
          }
        }

        if (addBtn) {
          if (pricing.availableCount > 0) {
            addBtn.disabled = false;
            var priceStr = '$' + (pricing.totalPrice / 100).toFixed(2);
            if (pricing.availableCount === pricing.totalProducts) {
              addBtn.innerHTML = 'Add Experience <span class="departure-experience-price">' + priceStr + '</span>';
            } else {
              addBtn.innerHTML = 'Add ' + pricing.availableCount + ' Available <span class="departure-experience-price">' + priceStr + '</span>';
            }
          } else {
            addBtn.disabled = true;
            addBtn.innerHTML = 'All Products Unavailable';
          }
        }
      }
    }

    // Update card-level buttons (for mobile where experience bar is hidden)
    updateCardAddButtons(pricing);

    // Update departure card price badge with dynamic pricing
    updateDepartureCardPriceBadge(pricing);
  }

  window.updateExperienceBar = updateExperienceBar;


  /* ========================================================================
     CARD-LEVEL ADD BUTTONS — transform departure card buttons in products mode
     ======================================================================== */

  function transformCardButtons(selectedCardEl) {
    if (!selectedCardEl) return;

    // Mark card as in products mode for CSS
    selectedCardEl.dataset.departureMode = 'products';

    // Save original button HTML for later restoration
    var cardName = selectedCardEl.dataset.cardName || '';
    var btns = selectedCardEl.querySelectorAll('.departure-view-collection-btn');
    btns.forEach(function(btn, i) {
      if (!departureState.originalBtnHTML[cardName + '_' + i]) {
        departureState.originalBtnHTML[cardName + '_' + i] = btn.innerHTML;
      }
      btn.classList.add('departure-card-add-mode');
      // Remove the View Experience click data so it doesn't re-trigger navigation
      btn.dataset.addMode = 'true';
    });

    // Inject mobile back button into the card's info area
    if (!selectedCardEl.querySelector('.departure-card-back-btn')) {
      var backBtn = document.createElement('button');
      backBtn.className = 'departure-card-back-btn';
      backBtn.innerHTML = '← Destinations';
      var infoContent = selectedCardEl.querySelector('.product-info-content');
      if (infoContent) {
        infoContent.insertBefore(backBtn, infoContent.firstChild);
      }
    }
  }

  function restoreCardButtons() {
    document.querySelectorAll('.departure-view-collection-btn.departure-card-add-mode').forEach(function(btn) {
      btn.classList.remove('departure-card-add-mode', 'departure-btn-adding', 'departure-btn-success');
      btn.disabled = false;
      btn.dataset.addMode = '';
    });

    // Remove mobile back buttons and clear products mode attribute
    document.querySelectorAll('.departure-card').forEach(function(card) {
      delete card.dataset.departureMode;
    });
    document.querySelectorAll('.departure-card-back-btn').forEach(function(btn) {
      btn.remove();
    });

    // Restore original HTML
    document.querySelectorAll('.departure-card').forEach(function(card) {
      var cardName = card.dataset.cardName || '';
      var btns = card.querySelectorAll('.departure-view-collection-btn');
      btns.forEach(function(btn, i) {
        var key = cardName + '_' + i;
        if (departureState.originalBtnHTML[key]) {
          btn.innerHTML = departureState.originalBtnHTML[key];
        }
      });
    });

    departureState.originalBtnHTML = {};
  }

  function updateCardAddButtons(pricing) {
    if (!pricing) pricing = getExperiencePricing();

    document.querySelectorAll('.departure-view-collection-btn.departure-card-add-mode').forEach(function(btn) {
      // Skip if in adding/success state
      if (btn.classList.contains('departure-btn-adding') || btn.classList.contains('departure-btn-success')) return;

      if (pricing.availableCount > 0) {
        btn.disabled = false;
        if (pricing.availableCount === pricing.totalProducts) {
          btn.innerHTML = 'Add ' + pricing.totalProducts + ' Product' + (pricing.totalProducts !== 1 ? 's' : '');
        } else {
          btn.innerHTML = 'Add ' + pricing.availableCount + ' Available';
        }
      } else {
        btn.disabled = true;
        btn.innerHTML = 'All Products Unavailable';
      }
    });
  }


  /* ========================================================================
     DYNAMIC PRICE BADGE — updates the departure card's price badge
     to reflect currently active size toggles and availability.
     Same pricing source as the experience bar.
     ======================================================================== */

  function updateDepartureCardPriceBadge(pricing) {
    if (!pricing) pricing = getExperiencePricing();

    // Find the selected departure card
    var selectedCard = document.querySelector('.departure-card:not(.hidden)');
    if (!selectedCard) return;

    var badge = selectedCard.querySelector('.price-badge');
    if (!badge) return;

    if (pricing.availableCount > 0) {
      var priceStr = '$' + (pricing.totalPrice / 100).toFixed(2);
      badge.textContent = priceStr;
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
    }
  }

  function restorePriceBadges() {
    document.querySelectorAll('.departure-card').forEach(function(card) {
      var badge = card.querySelector('.price-badge');
      if (!badge) return;

      var totalCents = parseInt(card.dataset.totalCents) || 0;
      if (totalCents > 0) {
        badge.textContent = '$' + (totalCents / 100).toFixed(2);
        badge.style.display = '';
      }
    });
  }


  /* ========================================================================
     EVENT: Toggle size changes in departure products view → recalculate
     ======================================================================== */

  document.addEventListener('click', function(e) {
    var toggleBtn = e.target.closest('.size-toggle-btn');
    if (!toggleBtn) return;

    // Only recalculate if we're in departure products view
    if (departureState.mode !== 'products') return;

    // Small delay to let the main toggle handler fire first and update .active
    setTimeout(updateExperienceBar, 50);
  }, true);


  /* ========================================================================
     EVENT: "View Collection" on experience cards
     Only fires when button is NOT in add mode
     ======================================================================== */

  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.departure-view-collection-btn');
    if (!btn) return;

    // If button is in add mode, handle as Add Experience instead
    if (btn.dataset.addMode === 'true') return;

    e.stopPropagation();

    departureState.selectedCard = btn.dataset.cardName;
    departureState.selectedHook = btn.dataset.transportHook;
    departureState.linkedHandles = (btn.dataset.linkedHandles || '').split(',').filter(function(h) { return h.trim(); });
    departureState.mode = 'products';

    // Inflate any deferred collections that contain linked products
    if (typeof window.inflateCollection === 'function') {
      var jsonEl = document.getElementById('pairingHandleMap');
      if (jsonEl) {
        try {
          var map = JSON.parse(jsonEl.textContent);
          var inflated = {};
          departureState.linkedHandles.forEach(function(h) {
            var entry = map[h.trim()];
            if (entry && entry.collection && !inflated[entry.collection]) {
              inflated[entry.collection] = true;
              window.inflateCollection(entry.collection);
            }
          });
        } catch (e) { /* ignore */ }
      }
    }

    showDepartureLounge();

    if (typeof trackShowroom === 'function') {
      trackShowroom('departure_card_selected', {
        card_name: departureState.selectedCard,
        product_count: departureState.linkedHandles.length
      });
    }
  });


  /* ========================================================================
     EVENT: Card-level Add button click (when in add mode)
     ======================================================================== */

  document.addEventListener('click', async function(e) {
    var btn = e.target.closest('.departure-view-collection-btn[data-add-mode="true"]');
    if (!btn || btn.disabled) return;
    e.stopPropagation();

    await executeAddExperience(btn);
  });


  /* ========================================================================
     EVENT: Desktop "Add Experience" bar button click
     ======================================================================== */

  document.addEventListener('click', async function(e) {
    var btn = e.target.closest('#departureAddExperienceBtn');
    if (!btn || btn.disabled) return;
    e.stopPropagation();

    await executeAddExperience(btn);
  });


  /* ========================================================================
     ADD EXPERIENCE — shared logic for both desktop bar and card-level button
     ======================================================================== */

  async function executeAddExperience(btn) {
    btn.disabled = true;
    var originalHTML = btn.innerHTML;

    // Phase 1: Immediate feedback
    btn.innerHTML = 'Adding...';
    btn.classList.add('departure-btn-adding');

    try {
      var items = [];
      var handleSet = {};
      departureState.linkedHandles.forEach(function(h) {
        handleSet[h.trim().toLowerCase()] = true;
      });

      document.querySelectorAll('.product-card:not(.hidden):not(.departure-card):not(.cart-view-card)').forEach(function(card) {
        var handle = (card.dataset.productHandle || '').toLowerCase();
        if (!handleSet[handle]) return;

        var activeToggle = card.querySelector('.size-toggle-btn.active');
        if (!activeToggle) return;
        if (activeToggle.dataset.variantAvailable !== 'true') return;

        items.push({
          id: parseInt(activeToggle.dataset.variantId),
          quantity: 1
        });
      });

      if (items.length === 0) {
        if (typeof showToast === 'function') showToast('No available products to add');
        btn.classList.remove('departure-btn-adding');
        btn.disabled = false;
        btn.innerHTML = originalHTML;
        return;
      }

      // Phase 2: Toast while adding
      if (typeof showToast === 'function') {
        showToast('Adding ' + items.length + ' product' + (items.length !== 1 ? 's' : '') + ' to cart...');
      }

      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      });

      if (!response.ok) throw new Error('Cart add failed');

      // Phase 3: Update cart state (triggers all card-level updates)
      if (typeof fetchCart === 'function') await fetchCart();

      // Phase 4: Success state on button
      btn.innerHTML = 'Added ✓';
      btn.classList.remove('departure-btn-adding');
      btn.classList.add('departure-btn-success');

      // Also update all other add-mode buttons to match
      document.querySelectorAll('.departure-view-collection-btn.departure-card-add-mode').forEach(function(otherBtn) {
        if (otherBtn !== btn) {
          otherBtn.innerHTML = 'Added ✓';
          otherBtn.classList.add('departure-btn-success');
        }
      });

      // Update desktop bar button too
      var desktopBtn = document.getElementById('departureAddExperienceBtn');
      if (desktopBtn && desktopBtn !== btn) {
        desktopBtn.innerHTML = 'Added ✓';
        desktopBtn.classList.add('departure-btn-success');
      }

      if (typeof showToast === 'function') {
        showToast(escapeHtml(departureState.selectedCard) + ' — ' + items.length + ' product' + (items.length !== 1 ? 's' : '') + ' added!');
      }

      if (typeof trackShowroom === 'function') {
        trackShowroom('departure_add_experience', {
          card_name: departureState.selectedCard,
          products_added: items.length
        });
      }

      // Phase 5: Revert to dynamic pricing after 2 seconds
      setTimeout(function() {
        btn.classList.remove('departure-btn-success');
        btn.disabled = false;

        document.querySelectorAll('.departure-view-collection-btn.departure-card-add-mode').forEach(function(b) {
          b.classList.remove('departure-btn-success');
          b.disabled = false;
        });

        var dBtn = document.getElementById('departureAddExperienceBtn');
        if (dBtn) {
          dBtn.classList.remove('departure-btn-success');
          dBtn.disabled = false;
        }

        updateExperienceBar();
      }, 2000);

      return;

    } catch (err) {
      console.error('Add Experience failed:', err);
      if (typeof showToast === 'function') showToast('Something went wrong. Try again.');
      btn.classList.remove('departure-btn-adding');
      btn.disabled = false;
      btn.innerHTML = originalHTML;
    }
  }


  /* ========================================================================
     EVENT: "← Destinations" back button (scene bar)
     ======================================================================== */

  document.addEventListener('click', function(e) {
    if (!e.target.closest('#departureBackBtn')) return;
    e.stopPropagation();

    departureState.mode = 'cards';
    showDepartureLounge();

    if (typeof trackShowroom === 'function') {
      trackShowroom('departure_back_to_cards', {});
    }
  });


  /* ========================================================================
     EVENT: "← Destinations" back button (mobile card-level)
     ======================================================================== */

  document.addEventListener('click', function(e) {
    if (!e.target.closest('.departure-card-back-btn')) return;
    e.stopPropagation();

    departureState.mode = 'cards';
    showDepartureLounge();

    if (typeof trackShowroom === 'function') {
      trackShowroom('departure_back_to_cards', { source: 'card' });
    }
  });


  /* ========================================================================
     EVENT: Experience card tab switching (Experience / What's Inside)
     ======================================================================== */

  document.addEventListener('click', function(e) {
    var tab = e.target.closest('.departure-card .card-tab');
    if (!tab) return;
    e.stopPropagation();

    var panel = tab.dataset.tab;
    var card = tab.closest('.departure-card');

    card.querySelectorAll('.card-tab').forEach(function(t) { t.classList.remove('active'); });
    tab.classList.add('active');

    card.querySelectorAll('.card-tab-panel').forEach(function(p) { p.classList.remove('active'); });
    var target = card.querySelector('[data-panel="' + panel + '"]');
    if (target) target.classList.add('active');

    // Update data attribute for CSS overlay system
    card.dataset.activeTab = panel;
  });


  /* ========================================================================
     RESET — called when switching to any other toggle
     ======================================================================== */

  window.resetDepartureState = function() {
    // Restore card buttons before clearing state
    restoreCardButtons();

    // Restore original grid order before clearing snapshot
    if (departureState.originalOrder) {
      var productGrid = document.getElementById('productGrid');
      if (productGrid) {
        departureState.originalOrder.forEach(function(child) {
          productGrid.appendChild(child);
        });
      }
    }

    departureState.selectedCard = null;
    departureState.selectedHook = null;
    departureState.linkedHandles = [];
    departureState.mode = 'cards';
    departureState.originalOrder = null;
    departureState.originalBtnHTML = {};

    var sceneBar = document.getElementById('departureSceneBar');
    var experienceBar = document.getElementById('departureExperienceBar');
    if (sceneBar) sceneBar.style.display = 'none';
    if (experienceBar) experienceBar.style.display = 'none';
  };


  /* ========================================================================
     UTILITY
     ======================================================================== */

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

})();